// Data Access Layer: User Resources
import { supabase } from '../supabase';
import type { 
  UserResource, 
  UserResourceInsert, 
  UserResourceUpdate, 
  UserResourceWithDetails 
} from '../../types/database';

/**
 * Get current user ID (adapt for your auth provider)
 */
async function getCurrentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
}

/**
 * Fetch all resources progress for the current user
 */
export async function getUserResources(): Promise<UserResourceWithDetails[]> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    return [];
  }

  const { data, error } = await supabase
    .from('user_resources')
    .select(`
      *,
      resource:resources(*)
    `)
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching user resources:', error);
    throw new Error(`Failed to fetch user resources: ${error.message}`);
  }

  return data || [];
}

/**
 * Add or update user resource progress
 */
export async function upsertUserResource(resource: UserResourceInsert): Promise<UserResource> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { data, error } = await supabase
    .from('user_resources')
    .upsert({
      ...resource,
      user_id: userId,
    })
    .select()
    .single();

  if (error) {
    console.error('Error upserting user resource:', error);
    throw new Error(`Failed to save resource progress: ${error.message}`);
  }

  return data;
}

/**
 * Update an existing user resource
 */
export async function updateUserResource(
  resourceId: string,
  updates: UserResourceUpdate
): Promise<UserResource> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { data, error } = await supabase
    .from('user_resources')
    .update(updates)
    .eq('user_id', userId)
    .eq('resource_id', resourceId)
    .select()
    .single();

  if (error) {
    console.error('Error updating user resource:', error);
    throw new Error(`Failed to update resource progress: ${error.message}`);
  }

  return data;
}

/**
 * Mark a resource as started
 */
export async function startResource(resourceId: string): Promise<UserResource> {
  return upsertUserResource({
    user_id: '', // Will be set in upsertUserResource
    resource_id: resourceId,
    status: 'InProgress',
    started_at: new Date().toISOString(),
  });
}

/**
 * Mark a resource as completed
 */
export async function completeResource(resourceId: string): Promise<UserResource> {
  return updateUserResource(resourceId, {
    status: 'Completed',
    completed_at: new Date().toISOString(),
  });
}

/**
 * Delete user resource progress
 */
export async function deleteUserResource(resourceId: string): Promise<void> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { error } = await supabase
    .from('user_resources')
    .delete()
    .eq('user_id', userId)
    .eq('resource_id', resourceId);

  if (error) {
    console.error('Error deleting user resource:', error);
    throw new Error(`Failed to delete resource progress: ${error.message}`);
  }
}
